import React, { useState, useEffect, useCallback } from 'react';
// Use native alerts to match Weaknesses behaviour
import buttonImage from '../buttonimage.webp';
import backgroundImage from '../background.webp';
import cardBackgroundImage from '../cardbackground.webp';
import { supabase } from '../supabaseClient';

function Dream({ goBack, goHome, handleSaveProgress }) {

    const handleSave = async () => {
        if (typeof handleSaveProgress === 'function') {
            try {
                await handleSaveProgress('dream', null, false);
                alert('Session progress has been saved successfully!');
            } catch (err) {
                console.error('Error saving progress:', err);
                alert('Failed to save session progress.');
            }
        }
    };

    const [worksheetText, setWorksheetText] = useState('');
    const [worksheetSaving, setWorksheetSaving] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');

    const retrySupabaseQuery = useCallback(async (queryFn, retries = 3, delay = 1000) => {
        for (let attempt = 1; attempt <= retries; attempt++) {
            try {
                return await queryFn();
            } catch (error) {
                if (attempt < retries) {
                    await new Promise((resolve) => setTimeout(resolve, delay));
                } else {
                    throw error;
                }
            }
        }
    }, []);

    const fetchWorksheet = useCallback(async () => {
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                console.error('User not logged in');
                return;
            }
            const { data, error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('api')
                    .from('worksheets')
                    .select('dream')
                    .eq('id', user.id)
                    .single()
            );
            if (error) {
                console.error('Error loading worksheet:', error.message);
                setWorksheetText('');
                return;
            }
            setWorksheetText(data?.dream ?? '');
        } catch (err) {
            console.error('Error fetching worksheet:', err?.message || err);
            setWorksheetText('');
        }
    }, [retrySupabaseQuery]);

    useEffect(() => { fetchWorksheet(); }, [fetchWorksheet]);

    const handleSaveWorksheet = async () => {
        if (worksheetText.length > 1000) {
            setStatusMessage('Worksheet text cannot exceed 1000 characters.');
            return;
        }
        if (!worksheetText.trim()) {
            setStatusMessage('Worksheet text cannot be empty.');
            return;
        }
        setWorksheetSaving(true);
        setStatusMessage('');
        const pendingText = worksheetText.trim();
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                setStatusMessage('User not logged in. Please log in to save worksheet.');
                return;
            }
            const payload = { id: user.id, dream: pendingText };
            const { error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('api')
                    .from('worksheets')
                    .upsert(payload, { onConflict: 'id' })
            );
            if (error) { throw error; }
            setStatusMessage('Worksheet saved successfully.');
            await new Promise(resolve => setTimeout(resolve, 500));
            await fetchWorksheet();
        } catch (err) {
            setStatusMessage('Failed to save worksheet. Retaining unsaved text.');
            console.error('Error saving worksheet:', err?.message || err);
            setWorksheetText(pendingText);
        } finally {
            setWorksheetSaving(false);
        }
    };

    return (
        <div style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '16px',
        }}>
            {/* Using native alerts for confirmations to match Weaknesses */}
            {/* Title */}
            <h3 style={{
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '24px',
                fontWeight: 'bold',
                color: 'black',
                marginBottom: '32px',
            }}>
                Dream
            </h3>

            {/* WorksheetCard Section */}
                {/* Worksheet Section (Weaknesses-style inline) */}
                <form
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '16px',
                        width: '100%',
                        maxWidth: '600px',
                        marginBottom: '24px',
                    }}
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSaveWorksheet();
                    }}
                >
                    <h3 id="dream-worksheet-heading" style={{ fontFamily: 'Times New Roman, Times, serif', fontSize: '18px', fontWeight: 'bold', margin: 0 }}>Dream Worksheet</h3>
                    <label htmlFor="dream-worksheet" style={{ fontFamily: 'Times New Roman, Times, serif', fontSize: '16px', fontWeight: 'bold', alignSelf: 'flex-start' }}>
                        Enter your dream notes below.
                    </label>
                    <textarea
                        id="dream-worksheet"
                        name="dreamWorksheet"
                        value={worksheetText}
                        onChange={(e) => {
                            setWorksheetText(e.target.value);
                            setStatusMessage('');
                        }}
                        aria-labelledby="dream-worksheet-heading"
                        aria-describedby="dream-worksheet-help dream-worksheet-limit"
                        aria-required="true"
                        style={{ width: '100%', height: '220px', padding: '8px', borderRadius: '6px', border: '1px solid #4B5563', fontFamily: 'Times New Roman, Times, serif' }}
                        placeholder="Enter your dream (max 1000 characters)..."
                    />
                    <p id="dream-worksheet-help" style={{ margin: 0, fontFamily: 'Times New Roman, Times, serif', fontSize: '14px', alignSelf: 'flex-start' }}>
                        This is a live editable worksheet. Use the Save Worksheet button to save your changes.
                    </p>
                    <p id="dream-worksheet-limit" style={{ margin: '-8px 0 0', fontFamily: 'Times New Roman, Times, serif', fontSize: '14px', alignSelf: 'flex-start' }}>
                        Maximum 1000 characters.
                    </p>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                            type="submit"
                            style={{
                                backgroundImage: `url(${buttonImage})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                border: '2px solid black',
                                borderRadius: '10px',
                                padding: '10px 18px',
                                color: 'black',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                                minWidth: '140px',
                                opacity: worksheetSaving ? 0.7 : 1,
                            }}
                            disabled={worksheetSaving}
                        >
                            {worksheetSaving ? 'Saving...' : 'Save Worksheet'}
                        </button>
                    </div>
                    <div role="status" aria-live="polite" aria-atomic="true" style={{ minHeight: '1.5em', fontFamily: 'Times New Roman, Times, serif', fontSize: '14px' }}>
                        {statusMessage}
                    </div>
                </form>

            {/* Cards */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                width: '100%',
                maxWidth: '600px',
            }}>
                {/* Card 1 */}
                <div style={{
                    width: '100%',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'center',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }}>
                    <p style={{
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        color: 'black',
                    }}>
                        Our sleep time dreams have messages for us.  They can reflect our subconscious back to us.There are many methods for dream interpretation but the right one is the one that works for you.  I have not included a lot about dreams in this app, but I did want to acknowledge the topic.
                    </p>
                </div>

                {/* Card 2 */}
                <div style={{
                    width: '100%',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'center',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }}>
                    <p style={{
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        color: 'black',
                    }}>
                        Dreams can also be a way we describe our imagined desired future aspirations. We might be called to something from our soul or our heart and know it is meant for us.  We can also just use our imagination to create dreams for ourselves to live in.  
                    </p>
                </div>

                {/* Card 3 */}
                <div style={{
                    width: '100%',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'center',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }}>
                    <p style={{
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        color: 'black',
                    }}>
                        The only thing I can really offer about dreams is the idea that dreams allow us to know there is more than just this physical reality.  There are worlds that have not even materialized yet.  It gives us a tangible way to see that thoughts become things.  Energy is in motion even before its creation manifests.  What we think about matters. 
                    </p>
                </div>
            </div>

            {/* Navigation Buttons */}
            <div
                className="dream-nav-btn-row"
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '16px',
                    marginTop: '32px',
                    marginBottom: '16px',
                }}
            >
                <button style={{
                    backgroundImage: `url(${buttonImage})`,
                    backgroundSize: 'cover',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '10px 20px',
                    width: '120px',
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }} onClick={goBack}>Back</button>
                <button style={{
                    backgroundImage: `url(${buttonImage})`,
                    backgroundSize: 'cover',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '10px 20px',
                    width: '120px',
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }} onClick={goHome}>Home</button>
                <button style={{
                    backgroundImage: `url(${buttonImage})`,
                    backgroundSize: 'cover',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '10px 20px',
                    width: '120px',
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }} onClick={handleSave}>Save</button>
            </div>
            <style>
                {`
                @media (max-width: 600px) {
                    .dream-nav-btn-row {
                        position: static !important;
                        width: 100%;
                        background: none;
                        border-top: none;
                        border-radius: 0;
                        box-shadow: none;
                        padding: 0;
                        z-index: auto;
                        display: flex;
                        justify-content: center;
                        gap: 10px;
                        margin-top: 32px !important;
                        margin-bottom: 16px !important;
                    }
                    body {
                        padding-bottom: 0 !important;
                    }
                }
                `}
            </style>
        </div>
    );
}

export default Dream;
